import * as React from "react";
import classNames from "classnames";
import CtaButton from "../cta-button/cta-button";
import Title from "~/components/title/title";
import ResponsiveImage from "../responsive-image/responsive-image";
import ArrowLink from "../arrow-link/arrow-link";
import { VerticalAlignmentType } from "~/types/alignment";
import ContentSection from "../content-section/content-section";

export interface ImageTextCtaReusableProps {
    imageSrc?: string;
    imageAltText?: string;
    title?: string;
    richText?: string;
    ctaLink?: string;
    ctaUrl?: string;
    ctaTarget?: string;
    showCtaAsLink?: boolean;
    useCtaAsImageLink?: boolean;
}

export interface ImageTextCtaProps extends ImageTextCtaReusableProps {
    isTitleH1?: boolean;
    trumpet?: string;
    dateDay?: string;
    dateMonth?: string;
    flipContent?: boolean;
    verticalAlignContent?: VerticalAlignmentType;
    enableThemeBackground?: boolean;
    fadeOverflowingRichText?: boolean;
    styleWithProductTheme?: boolean;
    anchorId?: string;
    className?: string;
}

const ImageTextCta = ({
    imageSrc,
    imageAltText,
    trumpet,
    title,
    isTitleH1,
    richText,
    ctaLink,
    ctaUrl,
    ctaTarget,
    useCtaAsImageLink,
    showCtaAsLink,
    dateDay,
    dateMonth,
    flipContent,
    verticalAlignContent,
    enableThemeBackground,
    fadeOverflowingRichText,
    styleWithProductTheme,
    className,
    ...props
}: ImageTextCtaProps) => {
    const baseClass = "image-text-cta";
    const componentClasses = classNames(baseClass, className, {
        [`${baseClass}--site-theming`]: !styleWithProductTheme,
        [`${baseClass}--no-image`]: !imageSrc,
        [`${baseClass}--flip-content`]: flipContent,
        [`${baseClass}--theme-background`]: enableThemeBackground
    });
    const componentInnerClasses = classNames(`${baseClass}__inner`, {
        [`vertical-align-${verticalAlignContent}`]: verticalAlignContent
    });
    const trumpetCircleClasses = classNames(`${baseClass}__trumpet-circle`, {
        "theme-bg-light": styleWithProductTheme
    });
    const trumpetTextClasses = classNames(`${baseClass}__trumpet-text`, {
        "theme-color-dark": styleWithProductTheme
    });
    const richTextClasses = classNames(`${baseClass}__rich-text content-rich-text`, {
        [`${baseClass}__rich-text--fade-overflow`]: fadeOverflowingRichText
    });
    const Image = () =>
        imageSrc ? (
            <ResponsiveImage
                srcUrl={imageSrc}
                defaultSize={345}
                srcSetSizes={[264, 345, 438]}
                sizes="(max-width: 480px) 55vw, (max-width: 768px) 45vw, (max-width: 1366px) 32vw, 415px"
                alt={imageAltText}
            />
        ) : null;

    return (
        <ContentSection
            className={componentClasses}
            type={enableThemeBackground ? "full" : "default"}
            {...props}
        >
            <div className={componentInnerClasses}>
                {imageSrc && (
                    <div className={`${baseClass}__image-wrapper`}>
                        {ctaUrl ? (
                            <a href={ctaUrl} target={ctaTarget}>
                                <Image />
                            </a>
                        ) : (
                            <Image />
                        )}
                    </div>
                )}
                <div className={`${baseClass}__content-wrapper`}>
                    {trumpet && (
                        <div className={`${baseClass}__trumpet`}>
                            <span className={trumpetCircleClasses} />
                            <p className={trumpetTextClasses}>{trumpet}</p>

                            {dateDay && dateMonth && (
                                <div className={`${baseClass}__date`}>
                                    <div className={`${baseClass}__date-month`}>{dateMonth}</div>
                                    <div className={`${baseClass}__date-day`}>{dateDay}.</div>
                                </div>
                            )}
                        </div>
                    )}

                    {title && (
                        <Title title={title} isTitleH1={isTitleH1} linkHref={ctaUrl} linkTarget={ctaTarget} />
                    )}

                    {richText && (
                        <div className={richTextClasses} dangerouslySetInnerHTML={{ __html: richText }} />
                    )}

                    {ctaLink && !useCtaAsImageLink && (
                        <>
                            {showCtaAsLink ? (
                                <ArrowLink className={`${baseClass}__link`} link={ctaLink} />
                            ) : (
                                <CtaButton className={`${baseClass}__btn`} link={ctaLink} isLarge />
                            )}
                        </>
                    )}
                </div>
            </div>
        </ContentSection>
    );
};

export default ImageTextCta;
